export type FormulaNameAutocompleteSuggestion = {
  name: string
  scopeLabel?: string
  refersTo?: string
  hidden?: boolean
}

export type FormulaNameAutocomplete = {
  query: string
  startOffset: number
  suggestions: FormulaNameAutocompleteSuggestion[]
}

const FORMULA_NAME_RE = /([A-Za-z_\\][A-Za-z0-9_.\\]*)$/

const isFormulaNameContextCharacter = (char: string | undefined): boolean => (
  !char || char === '=' || char === '(' || char === ',' || /[+\-*/^&=<>{}:]/.test(char)
)

export function getFormulaNameAutocompleteQuery(
  text: string,
  caretOffset: number,
): { query: string; startOffset: number } | null {
  if (!String(text || '').trimStart().startsWith('=')) return null
  const before = text.slice(0, Math.max(0, caretOffset))
  const bracketStart = before.lastIndexOf('[')
  if (bracketStart >= 0 && !before.slice(bracketStart + 1).includes(']')) {
    const previous = before.slice(0, bracketStart).trimEnd().at(-1)
    if (isFormulaNameContextCharacter(previous)) {
      return {
        query: before.slice(bracketStart),
        startOffset: bracketStart,
      }
    }
  }
  const match = FORMULA_NAME_RE.exec(before)
  if (!match) return null
  const query = match[1]
  const startOffset = before.length - query.length
  const previous = before.slice(0, startOffset).trimEnd().at(-1)
  if (!isFormulaNameContextCharacter(previous)) return null
  return { query, startOffset }
}

export function getFormulaNameAutocomplete(
  text: string,
  caretOffset: number,
  suggestions: FormulaNameAutocompleteSuggestion[],
): FormulaNameAutocomplete | null {
  const autocomplete = getFormulaNameAutocompleteQuery(text, caretOffset)
  if (!autocomplete) return null
  const query = autocomplete.query.toLowerCase()
  const bracketQuery = query.startsWith('[') ? query.slice(1) : null
  const matches = suggestions
    .filter((entry) => {
      if (entry.hidden === true) return false
      const name = entry.name.toLowerCase()
      if (bracketQuery !== null) {
        return name.startsWith('[') && name.slice(1).startsWith(bracketQuery)
      }
      return name.startsWith(query)
    })
    .slice(0, 8)
  return matches.length > 0 ? { ...autocomplete, suggestions: matches } : null
}
